import { ageClass } from "../lib/panelHeader";
import type { BriefAction } from "../lib/mayorBrief";
import type { DeskStateCell } from "../lib/mayorDesk";

/**
 * The state strip — the four conditions of the town, side by side.
 *
 * It is one compartment, not four panels: the four are a comparison, and a
 * comparison reads as a row. Each cell keeps its own level word — the
 * subject and the severity, never a bare severity — one figure, the source,
 * the reference time, and the way through to the evidence.
 *
 * A condition that is only partly covered says so. "No data" would be a
 * different claim, and a bare "Normal" over a partial count would be a false
 * all-clear.
 */

interface Props {
  cells: DeskStateCell[];
  /** Only an alert or a critical condition raises a check. */
  actions: BriefAction[];
  now: Date;
  onOpenEvidence: (evidence: string) => void;
}

/** A condition's reference time, in the reader's locale and time. */
function referenceTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  return new Date(t).toLocaleString("th-TH", {
    timeZone: "Asia/Bangkok",
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function DeskStateStrip({ cells, actions, now, onOpenEvidence }: Props) {
  return (
    <section className="desk-state" aria-label="สภาพของเมือง / Condition of the municipality">
      {cells.map((cell) => (
        <article
          key={cell.key}
          className={`desk-cell desk-cell--${cell.level}`}
          title={`${cell.fullTh} · ${cell.fullEn}`}
        >
          <h3 className="desk-cell-title">
            {cell.titleTh}
            <span>{cell.titleEn}</span>
          </h3>
          <p className="desk-cell-level">
            {cell.labelTh}
            <span>{cell.labelEn}</span>
          </p>
          {cell.figureTh && <p className="desk-cell-figure">{cell.figureTh}</p>}
          <div className="desk-cell-foot">
            <p className="desk-cell-provenance mono">
              <span>{cell.source}</span>
              {cell.observedAt ? (
                <span
                  className={`data-age ${ageClass(
                    Math.max(0, Math.round((now.getTime() - Date.parse(cell.observedAt)) / 60_000)),
                  )}`}
                >
                  {referenceTime(cell.observedAt)}
                </span>
              ) : (
                <span className="data-age data-age--crit">ไม่ทราบเวลาอ้างอิง</span>
              )}
            </p>
            <button
              type="button"
              className="desk-cell-evidence"
              onClick={() => onOpenEvidence(cell.evidence)}
            >
              ดูหลักฐาน · Evidence ↗
            </button>
          </div>
        </article>
      ))}
      {actions.length > 0 && (
        <div className="desk-checks">
          <h3>ประเด็นที่ควรตรวจสอบต่อ · Points to check next</h3>
          <ul>
            {actions.map((action) => (
              <li key={action.id}>
                <span>{action.th}</span>
                <span className="desk-action-en">{action.en}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
